import React, { useState } from 'react';
import { useDidMount } from 'rooks';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { clearLastLevelSummary } from 'a/hub/actions/workshopActions';
import useGlobalSfx from 'root/lib/hooks/useGlobalSfx';
import svgMap from './svgs/LevelSummary/svgMap';
import { ContinueButton } from '../sharedComponents';

const Panel = styled.img`
    position: absolute;
    top: 62px;
    left: 266px;
    object-fit: contain;
`;

const burstRotation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Burst = styled.img`
    animation: ${burstRotation} 20s linear infinite;
    position: absolute;
    top: -320px;
    left: -110px;
    transform-origin: center center;
`;

const Text = styled.div`
  color: #ffffff;
  font-family: Quicksand;
  text-align: center;
`;

const TitleText = styled(Text)`
  margin: 0;
  position: absolute;
  font-family: Quicksand Bold;
  top: 82px;
  left: 390px;
  width: 242px;
  height: 32px;
  font-size: 27px;
  font-weight: bold;
  letter-spacing: 1.2px;
  text-align: center;
`;

const Oval = styled.div`
  position: absolute;
  left: 410;
  top: 160;
  width: 200px;
  height: 200px;
  border: ${props => `solid 10px ${(props.isSuccess ? '#499689' : '#a17560')}`};
  background-color: rgba(255, 255, 255, 0);
  border-radius: 50%;
`;

const SuccessBottomText = styled(Text)`
    top: 385px;
    left: 310;
    position: absolute;
    width: 400px;
    height: 28px;
    font-size: 22px;
    font-weight: bold;
    text-align: center;
`;

const FailBottomText = styled(Text)`
    position: absolute;
    top:372px;
    left: 277px;
    width: 470px;
    font-size: 22px;
    font-weight: bold;
    text-align: center;
    padding: 0 80px;
`;

const FractionContainer = styled.div`
    top: 212px;
    position: absolute;
    left: 425px;
    width: 170px;
    height: 200px;
    display: table;
    margin: 0 auto;
`;

const FractionNumber = styled(Text)`
    font-family: Quicksand Bold;
    display: table-cell;
    font-size: ${props => (props.isDenominator ? '54px' : '72px')};
    width: ${props => (props.isDenominator ? '73px' : '97px')};
`;

const Separator = styled(Text)`
    top: 15px;
    left: 82px;
    position: absolute;
    text-align: center;
    font-family: Quicksand;
    display: table-cell;
    font-size: 54px;
`;

const LevelSummaryModal = ({ config, closeModal }) => {
  const [isPressed, setIsPressed] = useState(false);
  const [withBurst, setWithBurst] = useState(false);
  const {
    isSuccess = false,
    correctCount = 0,
    totalCount = 0,
  } = config;

  const panelSVG = isSuccess ? svgMap.GreenPanel : svgMap.BrownPanel;
  const titleText = isSuccess ? 'SUCCESS' : 'ALMOST';
  const bottomText = isSuccess ? 'Keep going, you\'re growing!' : 'That was challenging.\nLet\'s keep practicing.';
  const BottomTextComponent = isSuccess ? SuccessBottomText : FailBottomText;

  const dispatch = useDispatch();
  const audioFunctions = useGlobalSfx();
  const onClickHandler = () => {
    if (!isPressed) {
      audioFunctions.tap();
      setIsPressed(true);
      dispatch(clearLastLevelSummary());
      closeModal();
    }
  };

  useDidMount(() => {
    audioFunctions.pop({
      continuePlayingAfterUnmount: false,
      onend: () => {
        if (isSuccess) {
          setWithBurst(true);
          audioFunctions.levelMasteryK3();
        }
      },
    });
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="level-summary-modal-title"
      data-dd-action-name="level-summary-modal"
    >
      {withBurst && <Burst src={svgMap.Burst} alt="" />}
      <Panel src={panelSVG} alt="" />
      <TitleText as="h2" id="level-summary-modal-title">{titleText}</TitleText>
      <Oval isSuccess={isSuccess} />
      <FractionContainer data-testid={`${correctCount}-${totalCount}-${isSuccess}-levelMastery`}>
        <FractionNumber>{correctCount}</FractionNumber>
        <Separator> / </Separator>
        <FractionNumber isDenominator>{totalCount}</FractionNumber>
      </FractionContainer>
      <BottomTextComponent>{bottomText}</BottomTextComponent>
      <ContinueButton
        onClick={onClickHandler}
        top={443}
        data-dd-action-name="level-summary-modal:continue"
      />
    </div>
  );
};

LevelSummaryModal.propTypes = {
  closeModal: PropTypes.func,
  correctCount: PropTypes.number,
  totalCount: PropTypes.number,
  isSuccess: PropTypes.bool,
  config: PropTypes.shape({
    isSuccess: PropTypes.bool,
    correctCount: PropTypes.number,
    totalCount: PropTypes.number,
  }).isRequired,
};

LevelSummaryModal.defaultProps = {
  closeModal: () => {},
  isSuccess: true,
  correctCount: 9,
  totalCount: 9,
};

export default LevelSummaryModal;
